import React, { useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import PacienteCitasList from './PacienteCitasList';
import DayScheduleContent from './DayScheduleContent';

const LIST_TABS = [
  { id: 'pending', label: 'Pendientes', icon: 'bi-clock-history' },
  { id: 'upcoming', label: 'Próximas', icon: 'bi-calendar-event' },
  { id: 'all', label: 'Activas', icon: 'bi-lightning-charge' },
];

const TAB_META = {
  pending: {
    icon: 'bi-clock-history',
    subtitle: 'Confirmá, mové o cancelá visitas; las citas canceladas liberan espacio en la agenda',
    summaryLabel: 'Por confirmar',
  },
  upcoming: {
    icon: 'bi-calendar-event',
    subtitle: 'Próximas visitas del consultorio. Podés moverlas o marcarlas como realizadas.',
    summaryLabel: 'Próximas',
  },
  all: {
    icon: 'bi-lightning-charge',
    subtitle: 'Todas las citas activas. Gestioná confirmaciones y horarios desde aquí.',
    summaryLabel: 'Activas',
  },
};

function getHeaderIcon(view, listTab, pacienteNombre) {
  if (view === 'schedule') return 'bi-grid-3x3-gap';
  if (pacienteNombre) return 'bi-person-lines-fill';
  return TAB_META[listTab]?.icon || 'bi-calendar2-week-fill';
}

function getHeaderSubtitle(view, listTab, pacienteNombre, pendingCount) {
  if (view === 'schedule') {
    return pacienteNombre
      ? `Elegí horario para ${pacienteNombre}. Arrastrá en la grilla o cambiá de día.`
      : 'Cambiá de día u horario en la grilla; las citas canceladas liberan espacio';
  }
  if (pacienteNombre) {
    return pendingCount > 0
      ? `${pendingCount} cita${pendingCount === 1 ? '' : 's'} pendiente${pendingCount === 1 ? '' : 's'} de confirmar`
      : 'Agendá y gestioná las visitas de este paciente';
  }
  return TAB_META[listTab]?.subtitle || 'Revisá y gestioná la agenda del consultorio';
}

export default function PacienteCitasSidePanel({
  show,
  onHide,
  title = 'Citas del paciente',
  pacienteNombre = '',
  citasPaciente = [],
  citasConsultorio = [],
  pacientes = [],
  equipo = [],
  pacienteId,
  diaSeleccionado,
  onDateChange,
  onAgendar,
  onEditarCita,
  onCancelar,
  onConfirmar,
  onMarcarRealizada,
  onNewCitaFromSchedule,
  onEditCitaFromSchedule,
  onChangeEstadoFromSchedule,
  initialView = 'list',
  highlightCitaId = null,
  listTab = 'pending',
  onListTabChange,
  showListTabs = false,
  listTabCounts = {},
  pacientesLookup = [],
  showPacienteEnTarjeta = false,
  onVerDetalleCita,
  emptyListMessage,
}) {
  const [view, setView] = useState(initialView);

  useEffect(() => {
    if (show) setView(initialView);
  }, [show, initialView]);

  useEffect(() => {
    if (!show || view !== 'schedule' || diaSeleccionado) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    onDateChange?.(today);
  }, [show, view, diaSeleccionado, onDateChange]);

  const schedulingHint = pacienteNombre
    ? `Elegí el horario para ${pacienteNombre}. Arrastrá en la grilla o cambiá de día.`
    : 'Elegí el horario en la grilla (arrastrá o hacé clic en una franja libre).';

  const pendingCount = listTabCounts.pending ?? 0;
  const currentTabCount = listTabCounts[listTab] ?? citasPaciente.length;
  const headerIcon = getHeaderIcon(view, listTab, pacienteNombre);
  const headerSubtitle = getHeaderSubtitle(view, listTab, pacienteNombre, pendingCount);
  const summaryLabel = TAB_META[listTab]?.summaryLabel || 'En vista';

  const openSchedule = (date) => {
    if (date) onDateChange?.(date);
    setView('schedule');
  };

  const openCitaDay = (cita) => {
    if (onVerDetalleCita) {
      onVerDetalleCita(cita);
      return;
    }
    if (!cita?.fechaHora) return;
    const d = new Date(cita.fechaHora);
    d.setHours(0, 0, 0, 0);
    onDateChange?.(d);
    setView('schedule');
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="paciente-citas-side-panel citas-pending-panel"
      scroll
      backdrop
    >
      <Offcanvas.Header closeButton className="citas-nueva-modal-header paciente-citas-side-panel__header">
        <div className="citas-nueva-modal-heading paciente-citas-side-panel__heading">
          {view !== 'list' ? (
            <button
              type="button"
              className="paciente-citas-side-panel__back"
              onClick={() => setView('list')}
              aria-label="Volver al listado de citas"
            >
              <i className="bi bi-arrow-left" />
            </button>
          ) : null}
          <Offcanvas.Title className="citas-nueva-modal-title">
            <span className="citas-nueva-modal-title-icon" aria-hidden>
              <i className={`bi ${headerIcon}`} />
            </span>
            <span className="citas-nueva-modal-title-text">
              {title}
              <small className="citas-nueva-modal-title-sub">{headerSubtitle}</small>
            </span>
          </Offcanvas.Title>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="citas-nueva-modal-body paciente-citas-side-panel__body p-0">
        {view === 'list' ? (
          <div className="citas-nueva-modal-inner paciente-citas-side-panel__inner">
            <div className="citas-nueva-preview citas-pending-preview">
              <div className="citas-nueva-preview-main">
                <div className="citas-nueva-preview-label">
                  <i className="bi bi-inboxes" aria-hidden />
                  {summaryLabel}
                </div>
                <div className="citas-nueva-preview-value">
                  <strong>{currentTabCount}</strong>
                  <span className="citas-pending-preview__text">
                    {currentTabCount === 1 ? 'cita en esta vista' : 'citas en esta vista'}
                  </span>
                </div>
              </div>
              {pendingCount > 0 && listTab === 'pending' ? (
                <div className="citas-nueva-preview-chip citas-pending-preview__chip" title="Esperan confirmación">
                  <i className="bi bi-exclamation-circle-fill" aria-hidden />
                  {pendingCount} por confirmar
                </div>
              ) : null}
            </div>

            {showListTabs ? (
              <div className="paciente-citas-side-panel__tabs" role="tablist" aria-label="Filtrar citas">
                {LIST_TABS.map((tab) => {
                  const count = listTabCounts[tab.id];
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={listTab === tab.id}
                      className={`paciente-citas-side-panel__tab${listTab === tab.id ? ' is-active' : ''}`}
                      onClick={() => onListTabChange?.(tab.id)}
                    >
                      <i className={`bi ${tab.icon}`} aria-hidden />
                      <span>{tab.label}</span>
                      {count != null && count > 0 ? (
                        <span className="paciente-citas-side-panel__tab-badge">{count > 99 ? '99+' : count}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : null}

            <div className="paciente-citas-side-panel__toolbar">
              <button type="button" className="citas-nueva-btn citas-nueva-btn--primary paciente-citas-side-panel__action" onClick={onAgendar}>
                <i className="bi bi-calendar-plus" />
                <span>Agendar</span>
              </button>
              <button
                type="button"
                className="citas-nueva-btn citas-nueva-btn--ghost paciente-citas-side-panel__action"
                onClick={() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  openSchedule(today);
                }}
              >
                <i className="bi bi-grid-3x3-gap" />
                <span>Horario hoy</span>
              </button>
            </div>

            <PacienteCitasList
              citas={citasPaciente}
              maxVisible={50}
              variant="drawer"
              onAgendar={onAgendar}
              onEditar={onEditarCita}
              onCancelar={onCancelar}
              onConfirmar={onConfirmar}
              onMarcarRealizada={onMarcarRealizada}
              onVerDetalle={openCitaDay}
              highlightCitaId={highlightCitaId}
              pacientes={showPacienteEnTarjeta ? pacientesLookup : null}
              emptyMessage={emptyListMessage}
              hideDrawerCount
            />
          </div>
        ) : (
          <div className="paciente-citas-side-panel__schedule-wrap">
            <DayScheduleContent
              date={diaSeleccionado}
              onDateChange={onDateChange}
              citas={citasConsultorio}
              pacientes={pacientes}
              equipo={equipo}
              onNewCita={onNewCitaFromSchedule}
              onChangeEstado={onChangeEstadoFromSchedule}
              onEditCita={onEditCitaFromSchedule}
              schedulingHint={schedulingHint}
              highlightPacienteId={pacienteId}
              closeOnSlotSelect={false}
              compactHeader
            />
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}
